import { authenticatedFetch } from '../api/client';
import { useChannelLabel } from '../context/ChannelNumberingContext';
import React, { useState, useEffect, useRef } from 'react';
import SpikeGrid from './SpikeGrid';
import Timeline from './Timeline';
import { synthesizeChannelTrace, applyDemoFilter } from '../data/demoDashboardData';
import {
  createSessionCacheKey,
  getOrLoadSessionCache,
} from '../utils/sessionCache';
import './SignalViewPanel.css';

const SignalViewPanel = ({
  demoMode = false,
  highlightedSpikes,
  linkedTimeRange,
  onTimeRangeChange,
  datasetInfo,
  demoSignalData,
  linkedSelectedChannels = [],
  onSelectedChannelsChange,
  clusterSpikes = [],
  dataCacheScope = '',
  onLoadingChange,
}) => {
  const labelChannel = useChannelLabel();
  // State management - exact copy from App.js pattern
  const [selectedChannels, setSelectedChannels] = useState([179, 181, 183]);
  const [channelScrollOffset, setChannelScrollOffset] = useState(0);
  const [spikeData, setSpikeData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState({ start: 0, end: 1000 });
  const [windowSize, setWindowSize] = useState(1000);
  const [channelView, setChannelView] = useState({ base: null, ranges: {} });
  // A timeline or linked selection change starts a fresh view for all channels.
  const channelTimeRanges = channelView.base === timeRange ? channelView.ranges : {};
  const channelRangesKey = JSON.stringify(channelTimeRanges);
  const [spikeThreshold, setSpikeThreshold] = useState(-25);
  const [invertData, setInvertData] = useState(false);
  const [usePrecomputedSpikes, setUsePrecomputedSpikes] = useState(false);
  const [precomputedAvailable] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState('raw');
  const [filterType, setFilterType] = useState('none');
  const [filteredLineColor, setFilteredLineColor] = useState('#FFD700');
  
  // Use datasetInfo from props, fallback to defaults
  const totalDataPoints = datasetInfo?.totalDataPoints || 3500000;
  const totalChannels = datasetInfo?.totalChannels || 385;

  const windowSizeRef = useRef(windowSize);
  const signalMainRef = useRef(null);
  const [signalHeight, setSignalHeight] = useState(0);
  const channelsPerView = Math.max(1, Math.min(
    selectedChannels.length,
    Math.floor(signalHeight / 180)
  ));

  useEffect(() => {
    const grid = signalMainRef.current?.querySelector('.spike-grid');
    if (!grid || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setSignalHeight(entry.contentRect.height);
    });
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setChannelScrollOffset((offset) => Math.min(
      offset, Math.max(0, selectedChannels.length - channelsPerView)
    ));
  }, [selectedChannels.length, channelsPerView]);

  useEffect(() => {
    const normalized = linkedSelectedChannels
      .map(Number)
      .filter((channelId, index, values) => (
        Number.isInteger(channelId)
        && channelId >= 1
        && channelId <= totalChannels
        && values.indexOf(channelId) === index
      ));
    setSelectedChannels((previous) => (
      previous.length === normalized.length
      && previous.every((channelId, index) => channelId === normalized[index])
        ? previous
        : normalized
    ));
  }, [linkedSelectedChannels, totalChannels]);

  useEffect(() => {
    onLoadingChange?.('signalView', isLoading, 'Loading signal data…');
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    windowSizeRef.current = windowSize;
  }, [windowSize]);

  useEffect(() => {
    const firstSpike = Array.isArray(highlightedSpikes) ? highlightedSpikes[0] : null;
    const spikeTime = Number(firstSpike?.time);

    if (!Number.isFinite(spikeTime)) {
      return;
    }

    const activeWindowSize = windowSizeRef.current;
    const nextStart = Math.max(0, Math.floor(spikeTime - activeWindowSize / 2));
    setTimeRange({
      start: nextStart,
      end: nextStart + activeWindowSize
    });
    const spikeChannel = Number(firstSpike?.channel);
    if (Number.isFinite(spikeChannel)) {
      setSelectedChannels((previous) => [
        spikeChannel,
        ...previous.filter((channelId) => String(channelId) !== String(spikeChannel)),
      ]);
      onSelectedChannelsChange?.([spikeChannel]);
      setChannelScrollOffset(0);
    }
  }, [highlightedSpikes, onSelectedChannelsChange]);

  useEffect(() => {
    const start = Number(linkedTimeRange?.start);
    const end = Number(linkedTimeRange?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;

    const boundedStart = Math.max(0, Math.floor(start));
    const boundedEnd = Math.min(totalDataPoints, Math.ceil(end));
    const nextWindowSize = Math.max(1, Math.min(10000, boundedEnd - boundedStart));
    setWindowSize(nextWindowSize);
    setTimeRange({ start: boundedStart, end: boundedStart + nextWindowSize });
  }, [linkedTimeRange, totalDataPoints]);

  useEffect(() => {
    onTimeRangeChange?.(timeRange);
  }, [onTimeRangeChange, timeRange]);

  // Demo synthesis: produce plottable data for any selected channel without
  // touching the backend.
  useEffect(() => {
    if (!demoMode) return;
    if (selectedChannels.length === 0) {
      setSpikeData({});
      return;
    }

    const synthesized = {};
    selectedChannels.forEach((channelId) => {
      const range = JSON.parse(channelRangesKey)[channelId] || timeRange;
      const startTime = Math.max(0, Math.floor(range.start));
      const trace = synthesizeChannelTrace(channelId, Math.max(1, Math.ceil(range.end) - startTime));
      const baseData = invertData ? trace.data.map((v) => -v) : trace.data;
      const filtered = applyDemoFilter(baseData, filterType);

      let renderedData = baseData;
      if (selectedDataType === 'filtered') renderedData = filtered;
      else if (selectedDataType === 'spikes' && filterType !== 'none') renderedData = filtered;

      const threshold = spikeThreshold ?? -2.0;
      const isSpike = renderedData.map((v) =>
        invertData ? v >= -threshold : v <= threshold
      );

      synthesized[channelId] = {
        data: renderedData,
        filteredData: filtered,
        isSpike,
        spikePeaks: trace.spikePeaks,
        startTime,
        endTime: startTime + renderedData.length
      };
    });
    setSpikeData(synthesized);
  }, [demoMode, selectedChannels, selectedDataType, filterType, spikeThreshold, invertData, timeRange, channelRangesKey]);

  // Fetch signal data when parameters change
  useEffect(() => {
    if (demoMode) return;
    if (selectedChannels.length === 0) return;

    let active = true;
    const ranges = JSON.parse(channelRangesKey);
    const fetchSignalData = async () => {
      setIsLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const entries = await Promise.all(selectedChannels.map(async (channelId) => {
          const range = ranges[channelId] || timeRange;
          // The API caps each request at 20,000 samples, including the buffer.
          const buffer = Math.max(0, Math.min(windowSize, Math.floor((20000 - (range.end - range.start)) / 2)));
          const fetchStart = Math.max(0, Math.floor(range.start) - buffer);
          const fetchEnd = Math.min(totalDataPoints, Math.ceil(range.end) + buffer);
          const cacheKey = createSessionCacheKey('widget-data', [
            dataCacheScope, 'signal-channel', channelId, fetchStart, fetchEnd,
            spikeThreshold, invertData, usePrecomputedSpikes, selectedDataType, filterType,
          ]);
          const channelData = await getOrLoadSessionCache(cacheKey, async () => {
            const response = await authenticatedFetch(`${apiUrl}/api/spike-data`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                channels: [channelId], spikeThreshold, invertData,
                startTime: fetchStart, endTime: fetchEnd,
                usePrecomputed: usePrecomputedSpikes,
                dataType: selectedDataType, filterType,
              }),
            });
            if (!response.ok) throw new Error(`Unable to load signal data (${response.status})`);
            const data = await response.json();
            return data[channelId];
          });
          return [channelId, channelData];
        }));
        if (active) setSpikeData(Object.fromEntries(entries));
      } catch (error) {
        if (active) console.error('Error fetching signal data:', error);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchSignalData();
    return () => { active = false; };
  }, [selectedChannels, timeRange, channelRangesKey, spikeThreshold, invertData,
      usePrecomputedSpikes, selectedDataType, filterType, totalDataPoints, windowSize, demoMode,
      dataCacheScope]);

  const handleChannelToggle = (channelId) => {
    setSelectedChannels(prev => {
      const next = prev.includes(channelId)
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId];
      onSelectedChannelsChange?.(next);
      return next;
    });
    setChannelScrollOffset(0);
  };

  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
  };

  const handleWindowSizeChange = (newSize) => {
    const currentStart = timeRange.start;
    setWindowSize(newSize);
    setTimeRange({ start: currentStart, end: currentStart + newSize });
  };

  const handleChannelTimeRangeChange = (channelId, range) => {
    setChannelView((previous) => {
      const ranges = previous.base === timeRange ? previous.ranges : {};
      if (!range) {
        const next = { ...ranges };
        delete next[channelId];
        return { base: timeRange, ranges: next };
      }
      const span = Math.min(totalDataPoints, 10000, Math.max(1, range.end - range.start));
      const start = Math.max(0, Math.min(totalDataPoints - span, range.start));
      const next = { start, end: start + span };
      if (ranges[channelId]?.start === next.start && ranges[channelId]?.end === next.end) return previous;
      return { base: timeRange, ranges: { ...ranges, [channelId]: next } };
    });
  };

  const handleChannelScroll = (newOffset) => {
    setChannelScrollOffset(newOffset);
  };

  const handleSpikeThresholdChange = (value) => {
    setSpikeThreshold(value);
  };

  const handleInvertDataChange = (checked) => {
    setInvertData(checked);
  };

  const handleUsePrecomputedChange = (checked) => {
    setUsePrecomputedSpikes(checked);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
  };

  const handleFilteredLineColorChange = (value) => {
    setFilteredLineColor(value);
  };

  return (
    <div className="signal-view-panel">
      {/* Header with controls - exact copy of VisualizationArea */}
      <div className="viz-header">
        <div className="time-controls">
          <label>Data Type:</label>
          <select
            className="data-type-select"
            value={selectedDataType}
            onChange={(e) => setSelectedDataType(e.target.value)}
          >
            <option value="raw">Raw Data</option>
            <option value="filtered">Filtered View</option>
            <option value="spikes">Detected Spikes View</option>
          </select>
          <label>Time Range:</label>
          <input
            type="number"
            className="time-input"
            value={Math.floor(timeRange.start)}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value)) {
                handleTimeRangeChange({ start: value, end: value + windowSize });
              }
            }}
            placeholder="Start"
          />
          <span>to</span>
          <input
            type="number"
            className="time-input"
            value={Math.floor(timeRange.end)}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value)) {
                handleTimeRangeChange({ start: value - windowSize, end: value });
              }
            }}
            placeholder="End"
          />
          <label>Window Size:</label>
          <input
            type="number"
            className="window-input"
            value={windowSize}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value > 0 && value <= 10000) {
                handleWindowSizeChange(value);
              }
            }}
            min="1"
            max="10000"
            placeholder="Window"
          />
          {selectedDataType === 'filtered' && (
            <>
              <label>Filter Type:</label>
              <select
                className="filter-type-select"
                value={filterType}
                onChange={(e) => handleFilterTypeChange(e.target.value)}
              >
                <option value="highpass">High-pass (300 Hz)</option>
                <option value="lowpass">Low-pass (3000 Hz)</option>
                <option value="bandpass">Band-pass (300-3000 Hz)</option>
                <option value="none">None</option>
              </select>
              <label>Line Color:</label>
              <select
                className="filter-color-select"
                value={filteredLineColor}
                onChange={(e) => handleFilteredLineColorChange(e.target.value)}
              >
                <option value="#FFD700">Gold</option>
                <option value="#FF6B6B">Red</option>
                <option value="#4ECDC4">Teal</option>
                <option value="#95E1D3">Mint</option>
                <option value="#FF8C42">Orange</option>
                <option value="#C77DFF">Purple</option>
                <option value="#7FFF00">Chartreuse</option>
                <option value="#FF1493">Deep Pink</option>
                <option value="#00CED1">Dark Turquoise</option>
                <option value="#FFFFFF">White</option>
              </select>
            </>
          )}
          {selectedDataType === 'spikes' && (
            <>
              <label>Filter Type:</label>
              <select
                className="filter-type-select"
                value={filterType}
                onChange={(e) => handleFilterTypeChange(e.target.value)}
              >
                <option value="none">None (Raw Data)</option>
                <option value="highpass">High-pass (300 Hz)</option>
                <option value="lowpass">Low-pass (3000 Hz)</option>
                <option value="bandpass">Band-pass (300-3000 Hz)</option>
              </select>
              <label>Spike Threshold:</label>
              <input
                type="number"
                className="threshold-input"
                value={spikeThreshold ?? ''}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue === '') {
                    handleSpikeThresholdChange(null);
                  } else {
                    const value = parseFloat(inputValue);
                    if (!isNaN(value)) {
                      handleSpikeThresholdChange(value);
                    }
                  }
                }}
                step="1"
              />
            </>
          )}
          <label className="invert-checkbox">
            <input
              type="checkbox"
              checked={invertData}
              onChange={(e) => handleInvertDataChange(e.target.checked)}
            />
            <span>Invert Data</span>
          </label>
          {selectedDataType === 'spikes' && !demoMode && (
            <label className="precomputed-checkbox" style={{ opacity: precomputedAvailable ? 1 : 0.5 }}>
              <input
                type="checkbox"
                checked={usePrecomputedSpikes}
                onChange={(e) => handleUsePrecomputedChange(e.target.checked)}
                disabled={!precomputedAvailable}
              />
              <span>Use Pre-computed Spikes {!precomputedAvailable && '(No spike times loaded)'}</span>
            </label>
          )}
        </div>
      </div>

      <div className="signal-content">
        {/* Channel Sidebar */}
        <div className="signal-sidebar">
          <div className="sidebar-header">
            <span>Channels ({selectedChannels.length}/{totalChannels})</span>
          </div>
          <div className="channel-grid">
            {Array.from({ length: totalChannels }, (_, i) => i + 1).map((channelId) => (
              <button
                key={channelId}
                className={`channel-button ${selectedChannels.includes(channelId) ? 'selected' : ''}`}
                onClick={() => handleChannelToggle(channelId)}
              >
                {labelChannel(channelId)}
              </button>
            ))}
          </div>
        </div>

        {/* Main Signal Display Area */}
        <div className="signal-main-area" ref={signalMainRef}>
          {/* Fit more waveforms as the widget grows vertically. */}
          <SpikeGrid
            spikeData={spikeData}
            selectedChannels={selectedChannels}
            channelScrollOffset={channelScrollOffset}
            timeRange={timeRange}
            windowSize={windowSize}
            channelTimeRanges={channelTimeRanges}
            onChannelTimeRangeChange={handleChannelTimeRangeChange}
            onChannelScroll={handleChannelScroll}
            isLoading={isLoading}
            selectedDataType={selectedDataType}
            filteredLineColor={filteredLineColor}
            usePrecomputedSpikes={usePrecomputedSpikes}
            onSpikeNavigation={null}
            filterType={filterType}
            channelsPerView={channelsPerView}
            highlightedSpikes={highlightedSpikes}
            clusterSpikes={clusterSpikes}
          />

          {/* Timeline - exact same as VisualizationArea */}
          <Timeline
            timeRange={timeRange}
            windowSize={windowSize}
            totalDataRange={totalDataPoints}
            onTimeRangeChange={handleTimeRangeChange}
          />
        </div>
      </div>
    </div>
  );
};

export default SignalViewPanel;
